import React, { useState, useRef, useEffect } from "react";
import { RBarChart } from "../../../ChartUtility/recharts";
import { scopeColorAt } from "./chartPalette";
import useScopeSummary from "../../common/hooks/useEmission";
import { useScopeSummariesForFys } from "../../common/hooks/useScopeSummariesForFys";
import { downloadPeriodExcel, downloadBtnStyle } from "./scopeExcel";
import * as S from "./scopeDashboard.styles";

const ACROSS_YEARS = "acrossYears";
const IN_YEAR = "inYear";
const BAR_COLOR = scopeColorAt(0);

/** Calendar month token from a "Jul '25" label. */
const calOf = (label) => String(label || "").split(" ")[0];

/** Sum of a monthly row's category values → that period's total. */
const rowTotal = (payload, predicate) => {
  const cats = Object.keys(payload?.categories || {});
  const row = (payload?.monthly || []).find(predicate);
  return row ? cats.reduce((s, k) => s + Number(row[k] || 0), 0) : 0;
};

const fyName = (f) => f.financial_year_value || f.name;

/**
 * Period comparison for a scope — not a two-group split but N bars:
 *   • Across years: one month (e.g. Jul) shown per financial year.
 *   • Within a year: several months shown for one financial year.
 * Shared Location filter. Reuses the scope-summary endpoint (one call per FY for
 * across-years; a single call for within-a-year).
 * @param {string} props.scopeKey
 * @param {object[]} props.financialYears
 * @param {{id:number,name:string}[]} props.locations
 * @param {number|string} [props.defaultFyId]
 */
export const PeriodCompare = ({ scopeKey, financialYears = [], locations = [], defaultFyId, compareByToggle }) => {
  const [mode, setMode] = useState(ACROSS_YEARS);
  const [commonLoc, setCommonLoc] = useState("");
  const [month, setMonth] = useState("");
  const [inYearFy, setInYearFy] = useState(String(defaultFyId ?? ""));
  const [months, setMonths] = useState([]);

  const locArr = commonLoc ? [Number(commonLoc)] : [];

  // Across-years: fetch every FY once; within-a-year: fetch the one FY.
  const fyIds = mode === ACROSS_YEARS ? financialYears.map((f) => f.id) : [];
  const { byFy, loading: loadingA, error: errorA, refresh: refreshA } = useScopeSummariesForFys(scopeKey, fyIds, locArr);
  const sYear = useScopeSummary(scopeKey, mode === IN_YEAR ? inYearFy || undefined : undefined, locArr);
  const pYear = sYear.scopeData?.[scopeKey];

  // Month options for the pickers.
  const monthOptsAcross = (() => {
    const base = byFy[defaultFyId] || Object.values(byFy).find(Boolean);
    const seen = new Set();
    const out = [];
    (base?.monthly || []).forEach((r) => {
      const c = calOf(r.month);
      if (c && !seen.has(c)) { seen.add(c); out.push(c); }
    });
    return out;
  })();
  const monthOptsInYear = (pYear?.monthly || []).map((r) => r.month);

  const loading = mode === ACROSS_YEARS ? loadingA : sYear.loading;
  const error = mode === ACROSS_YEARS ? errorA : sYear.error;

  // Bars.
  let bars = [];
  let axisLabel = "";
  if (mode === ACROSS_YEARS && month) {
    axisLabel = `${month} · by financial year`;
    bars = financialYears.map((f) => ({
      name: fyName(f),
      value: rowTotal(byFy[f.id], (r) => calOf(r.month) === month),
      color: BAR_COLOR,
    }));
  } else if (mode === IN_YEAR && months.length) {
    axisLabel = `${fyName(financialYears.find((f) => String(f.id) === String(inYearFy)) || {})} · by month`;
    bars = months.map((label) => ({
      name: label,
      value: rowTotal(pYear, (r) => r.month === label),
      color: BAR_COLOR,
    }));
  }

  const ready = bars.length > 0;
  const series = [{ key: "value", name: "Emissions", getColor: (row) => row.color, maxBarSize: 56 }];

  return (
    <>
      <S.Panel>
        <S.PanelHead>
          <S.PanelTitles>
            <S.PanelTitle>Filters</S.PanelTitle>
            <S.PanelSub>A period across years, or months within a year</S.PanelSub>
          </S.PanelTitles>
          {compareByToggle}
        </S.PanelHead>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginBottom: 4 }}>
        <S.Toggle role="tablist" aria-label="Period mode">
          <S.ToggleBtn type="button" role="tab" aria-selected={mode === ACROSS_YEARS} $active={mode === ACROSS_YEARS} onClick={() => setMode(ACROSS_YEARS)}>
            Same month · across years
          </S.ToggleBtn>
          <S.ToggleBtn type="button" role="tab" aria-selected={mode === IN_YEAR} $active={mode === IN_YEAR} onClick={() => setMode(IN_YEAR)}>
            Months · within a year
          </S.ToggleBtn>
        </S.Toggle>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 24, marginTop: 8 }}>
        {mode === ACROSS_YEARS ? (
          <Field label="Month">
            <select className="kpi-inline-select" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">Select month…</option>
              {monthOptsAcross.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Financial Year">
            <select className="kpi-inline-select" value={inYearFy} onChange={(e) => { setInYearFy(e.target.value); setMonths([]); }}>
              {financialYears.map((f) => <option key={f.id} value={f.id}>{fyName(f)}</option>)}
            </select>
          </Field>
        )}

        <Field label="Location">
          <select className="kpi-inline-select" value={commonLoc} onChange={(e) => setCommonLoc(e.target.value)}>
            <option value="">All Locations</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>

        {mode === IN_YEAR && (
          <Field label="Months">
            <MonthMultiSelect options={monthOptsInYear} selected={months} onChange={setMonths} />
          </Field>
        )}
      </div>
      </S.Panel>

      {ready && (
        <S.Panel style={{ marginTop: 16 }}>
          <S.PanelHead>
            <S.PanelTitles>
              <S.PanelTitle>Period comparison</S.PanelTitle>
              <S.PanelSub>{axisLabel} · kgCO₂e</S.PanelSub>
            </S.PanelTitles>
            <button
              type="button"
              style={downloadBtnStyle}
              onClick={() => downloadPeriodExcel({ scopeTitle: scopeKey, axisLabel, bars })}
            >
              ⬇ Download Excel
            </button>
          </S.PanelHead>
          {loading ? (
            <S.Skeleton aria-hidden="true" />
          ) : error ? (
            <div role="alert" style={{ fontSize: 13, color: "#b91c1c", padding: "20px 0", textAlign: "center" }}>
              {error}
              {mode === ACROSS_YEARS && (
                <button type="button" onClick={refreshA} style={{ marginLeft: 10, fontSize: 12, cursor: "pointer" }}>
                  Retry
                </button>
              )}
            </div>
          ) : (
            <RBarChart data={bars} xKey="name" series={series} height={300} unit="kgCO₂e" animate />
          )}
        </S.Panel>
      )}

      {!ready && (
        <S.Panel style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: "#64748b", padding: "20px 0", textAlign: "center" }}>
            {mode === ACROSS_YEARS
              ? "Pick a month to compare it across financial years."
              : "Pick one or more months to compare within the year."}
          </div>
        </S.Panel>
      )}
    </>
  );
};

const Field = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{label}</span>
    {children}
  </div>
);

/** Compact checkbox dropdown for picking several months. */
const MonthMultiSelect = ({ options = [], selected = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const label = selected.length === 0
    ? "Select months…"
    : selected.length === options.length
      ? "All months"
      : `${selected.length} months selected`;

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 220 }}>
      <button
        type="button"
        className="kpi-inline-select"
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", textAlign: "left" }}
      >
        <span>{label}</span>
        <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 8 }}>{open ? "▲" : "▾"}</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 999,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 12px 32px rgba(0,0,0,.13)", maxHeight: 260, overflowY: "auto", padding: "4px 0",
        }}>
          {options.length === 0 && <div style={{ padding: "8px 13px", fontSize: 12, color: "#94a3b8" }}>No months reported</div>}
          {options.map((o) => {
            const checked = selected.includes(o);
            return (
              <label key={o} style={{
                display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer", fontSize: 12,
                background: checked ? "#eff6ff" : "transparent", alignItems: "center",
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(o)} style={{ accentColor: "#3f88a5", width: 13, height: 13 }} />
                {o}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PeriodCompare;
